<template>
  <!-- A ticket, open: its title (the person who opened it may rename it),
       where it is in its lifecycle, the conversation, and what can be done
       next. Staff assign it and move it along — start, resolve, close,
       re-open; the organization closes it, confirms it once it is resolved,
       or re-opens it. `tk-modal` scopes the dialog's look in the organization
       apps (assets/style/ticket.css). The same component lives in the admin
       and in both organization apps. -->
  <div class="tk-modal" @dragenter="onStrayDrag" @dragover="onStrayDrag" @drop="onStrayDrop">
    <div
      ref="dialog"
      class="modal dn-modal"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="view-ticket-title"
      :class="{ show: showModal }"
      @keydown.esc.stop="closeModal()"
    >
      <div class="modal-dialog dn-modal-dialog dn-modal-dialog--lg vt-dialog">
        <div class="modal-content dn-modal-content vt-content">
          <div class="dn-modal-header vt-header" :class="{ 'vt-header--editing': editingSubject }">
            <div class="dn-modal-heading">
              <span class="dn-modal-glyph">
                <i class="pi pi-ticket"></i>
              </span>
              <div class="dn-modal-heading-text">
                <!-- The person who opened the ticket may rename it. Escape
                     anywhere in it cancels the rename, not the dialog. -->
                <form
                  v-if="editingSubject"
                  class="vt-title-form"
                  @submit.prevent="saveSubject"
                  @keydown.esc.stop.prevent="cancelSubjectEdit"
                >
                  <input
                    ref="subjectInput"
                    v-model="subjectDraft"
                    type="text"
                    class="form-control vt-title-input"
                    :maxlength="subjectMaxLength"
                    :disabled="savingSubject"
                    aria-label="Ticket title"
                  />
                  <button
                    type="submit"
                    class="vt-title-btn vt-title-btn--save"
                    :disabled="!canSaveSubject"
                    :title="savingSubject ? 'Saving…' : 'Save title'"
                    aria-label="Save title"
                  >
                    <span v-if="savingSubject" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <i v-else class="pi pi-check" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    class="vt-title-btn"
                    :disabled="savingSubject"
                    title="Cancel"
                    aria-label="Cancel editing the title"
                    @click="cancelSubjectEdit"
                  >
                    <i class="pi pi-times" aria-hidden="true"></i>
                  </button>
                </form>
                <!-- Kept while editing, out of sight, as what names the dialog. -->
                <div class="vt-title-row" :class="{ 'sr-only': editingSubject }">
                  <h5 id="view-ticket-title" class="dn-modal-title">{{ ticket.subject }}</h5>
                  <button
                    v-if="canEditSubject && !editingSubject"
                    ref="editSubjectButton"
                    type="button"
                    class="vt-title-btn"
                    title="Edit title"
                    aria-label="Edit title"
                    @click="startSubjectEdit"
                  >
                    <i class="pi pi-pencil" aria-hidden="true"></i>
                  </button>
                </div>
                <p class="dn-modal-subtitle">
                  Ticket #{{ ticket.number }}
                  <template v-if="organizationName && isStaffViewer"> · {{ organizationName }}</template>
                  · Opened by {{ requesterName }} {{ timeAgo(ticket.createdAt) }}
                </p>
              </div>
            </div>
            <div class="dn-modal-header-actions">
              <ticket-assignee v-if="isStaffViewer" :ticket="ticket" @assigned="onTicketChanged" />
              <span v-else class="status-pill tk-stage" :class="`tk-stage--${stage}`">{{ stageLabel }}</span>
              <button type="button" class="dn-modal-close" @click="closeModal()" aria-label="Close">
                <i class="pi pi-times"></i>
              </button>
            </div>
          </div>

          <div class="vt-lifecycle">
            <ticket-lifecycle
              :stage="stage"
              :stage-changed-at="ticket.stageChangedAt || null"
              :assignee-name="isStaffViewer ? assigneeName : ''"
            />
          </div>

          <div class="dn-modal-body vt-body">
            <ticket-conversation
              ref="conversation"
              :ticket="ticket"
              :can-reply="!isClosed"
              :team-name="teamName"
              @ticket-updated="onTicketUpdated"
              @sent="onSent"
              @read="onRead"
            />
          </div>

          <!-- Resolved, on the organization's side: is it fixed? -->
          <div v-if="!isStaffViewer && isResolved" class="dn-modal-footer vt-footer vt-footer--confirm">
            <p class="vt-confirm">
              <i class="pi pi-check-circle" aria-hidden="true"></i>
              <span><strong>{{ teamName }} resolved this ticket.</strong> Is everything working now?</span>
            </p>
            <div class="vt-actions">
              <button type="button" class="btn btn-custom-secondary vt-btn" :disabled="moving" @click="statusMove = MOVE_REOPENED">
                <i class="pi pi-replay" aria-hidden="true"></i>
                <span>No, re-open it</span>
              </button>
              <button type="button" class="btn btn-custom-primary vt-btn vt-btn--resolve" :disabled="moving" @click="confirmResolved">
                <span v-if="moving" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <template v-else>
                  <i class="pi pi-check" aria-hidden="true"></i>
                  <span>Yes, close the ticket</span>
                </template>
              </button>
            </div>
          </div>

          <div v-else class="dn-modal-footer vt-footer">
            <span class="vt-footer__meta">{{ footerNote }}</span>
            <div class="vt-actions">
              <!-- Staff: the next step is the primary one. -->
              <template v-if="isStaffViewer">
                <button
                  v-if="!isClosed && !isResolved"
                  type="button"
                  class="btn btn-custom-secondary vt-btn vt-btn--close"
                  :disabled="moving"
                  @click="statusMove = MOVE_CLOSED"
                >
                  <i class="pi pi-lock" aria-hidden="true"></i>
                  <span>Close</span>
                </button>
                <button
                  v-if="isResolved"
                  type="button"
                  class="btn btn-custom-secondary vt-btn"
                  :disabled="moving"
                  @click="statusMove = MOVE_REOPENED"
                >
                  <i class="pi pi-replay" aria-hidden="true"></i>
                  <span>Re-open</span>
                </button>
                <button
                  v-if="isWaiting"
                  type="button"
                  class="btn btn-custom-secondary vt-btn"
                  :disabled="moving"
                  @click="statusMove = MOVE_RESOLVED"
                >
                  <i class="pi pi-check-circle" aria-hidden="true"></i>
                  <span>Resolve</span>
                </button>
                <button
                  v-if="isWaiting"
                  type="button"
                  class="btn btn-custom-primary vt-btn"
                  :disabled="moving"
                  @click="startProgress"
                >
                  <span v-if="moving" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <template v-else>
                    <i class="pi pi-play" aria-hidden="true"></i>
                    <span>Start progress</span>
                  </template>
                </button>
                <button
                  v-if="stage === STAGE_IN_PROGRESS"
                  type="button"
                  class="btn btn-custom-primary vt-btn vt-btn--resolve"
                  :disabled="moving"
                  @click="statusMove = MOVE_RESOLVED"
                >
                  <i class="pi pi-check-circle" aria-hidden="true"></i>
                  <span>Mark resolved</span>
                </button>
                <button
                  v-if="isResolved"
                  type="button"
                  class="btn btn-custom-primary vt-btn"
                  :disabled="moving"
                  @click="statusMove = MOVE_CLOSED"
                >
                  <i class="pi pi-lock" aria-hidden="true"></i>
                  <span>Close ticket</span>
                </button>
              </template>
              <!-- The organization closes it, or re-opens it once closed. -->
              <button
                v-else-if="!isClosed"
                type="button"
                class="btn btn-custom-secondary vt-btn vt-btn--close"
                :disabled="moving"
                @click="statusMove = MOVE_CLOSED"
              >
                <i class="pi pi-lock" aria-hidden="true"></i>
                <span>Close ticket</span>
              </button>
              <button
                v-if="isClosed"
                type="button"
                class="btn btn-custom-primary vt-btn vt-btn--reopen"
                :disabled="moving"
                @click="statusMove = MOVE_REOPENED"
              >
                <i class="pi pi-lock-open" aria-hidden="true"></i>
                <span>Re-open ticket</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ticket-status-modal
      v-if="statusMove"
      :ticket-id="ticket.id"
      :move="statusMove"
      :viewer-side="isStaffViewer ? SIDE_STAFF : SIDE_ORGANIZATION"
      :team-name="teamName"
      @close="statusMove = null"
      @done="onTicketChanged"
    />
  </div>
</template>
<script>
import { CLEAR_TICKET_COMMENTS, EDIT_TICKET, SET_TICKET_STATUS, UPSERT_TICKET } from '@/store/action_types';
import { TICKET_SUBJECT_MAX_LENGTH, TOAST_GLOBAL_CONFIG } from '@/utils/const';
import { personName, timeAgo, TICKET_SIDE_ORGANIZATION, TICKET_SIDE_STAFF, viewerTicketSide } from '@/utils/ticket';
import {
  TICKET_MOVE_CLOSED,
  TICKET_MOVE_IN_PROGRESS,
  TICKET_MOVE_REOPENED,
  TICKET_MOVE_RESOLVED,
  TICKET_STAGE_ASSIGNED,
  TICKET_STAGE_CLOSED,
  TICKET_STAGE_IN_PROGRESS,
  TICKET_STAGE_RESOLVED,
  TICKET_STAGE_SUBMITTED,
  ticketStageLabel,
  ticketStageOf,
} from '@/utils/ticket_lifecycle';
import { refreshTicketUnread } from '@/utils/ticket_notifications';

import ticketConversation from '@/components/ticket/Ticket_Conversation.vue';
import ticketLifecycle from '@/components/ticket/Ticket_Lifecycle.vue';
import ticketAssignee from '@/components/ticket/Ticket_Assignee.vue';
import ticketStatusModal from '@/components/ticket/Ticket_Status_Modal.vue';

const isFileDrag = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');

export default {
  props: {
    selectedTicketToView: {
      type: Object,
      required: true,
    },
    // The support team, as the organization's app names it.
    teamName: {
      type: String,
      default: 'Screenkode',
    },
  },
  components: {
    ticketConversation,
    ticketLifecycle,
    ticketAssignee,
    ticketStatusModal,
  },
  emits: ['closeViewTicketModal', 'refreshTickets'],
  data() {
    return {
      showModal: true,
      // The move whose remark dialog is open: resolved, closed or reopened.
      statusMove: null,
      moving: false,
      editingSubject: false,
      subjectDraft: '',
      savingSubject: false,
      subjectMaxLength: TICKET_SUBJECT_MAX_LENGTH,
      MOVE_CLOSED: TICKET_MOVE_CLOSED,
      MOVE_REOPENED: TICKET_MOVE_REOPENED,
      MOVE_RESOLVED: TICKET_MOVE_RESOLVED,
      STAGE_IN_PROGRESS: TICKET_STAGE_IN_PROGRESS,
      SIDE_STAFF: TICKET_SIDE_STAFF,
      SIDE_ORGANIZATION: TICKET_SIDE_ORGANIZATION,
    };
  },
  computed: {
    // The ticket as it stands: what the list knows, under what the open
    // conversation last heard from the API (its stage, its read marks).
    ticket() {
      const id = this.selectedTicketToView.id;
      const listed = (this.$store.getters.tickets.data || []).find((item) => item.id === id);
      const current = this.$store.getters.ticketComments.ticket;
      const base = listed || this.selectedTicketToView;
      return current && current.id === id ? { ...base, ...current } : base;
    },
    stage() {
      return ticketStageOf(this.ticket);
    },
    stageLabel() {
      return ticketStageLabel(this.stage);
    },
    isClosed() {
      return this.stage === TICKET_STAGE_CLOSED;
    },
    isResolved() {
      return this.stage === TICKET_STAGE_RESOLVED;
    },
    // Not started on yet: in the queue, or assigned.
    isWaiting() {
      return this.stage === TICKET_STAGE_SUBMITTED || this.stage === TICKET_STAGE_ASSIGNED;
    },
    organizationName() {
      return this.ticket.organization?.name || '';
    },
    // Staff see which organization a ticket is from, and who looks after it —
    // in the admin. In an organization's app everyone is the organization.
    isStaffViewer() {
      return (this.ticket.viewerSide || viewerTicketSide(this.$store.getters.account.data)) === TICKET_SIDE_STAFF;
    },
    assigneeName() {
      const person = this.ticket.assignedTo;
      return person && person.id ? personName(person, '') : '';
    },
    requesterName() {
      return personName(this.ticket.user, 'Requester');
    },
    messageCountLabel() {
      const count = Number(this.ticket.messageCount) || 0;
      return count ? `${count} ${count === 1 ? 'message' : 'messages'}` : '';
    },
    // What the footer says beside its buttons.
    footerNote() {
      if (this.isStaffViewer || this.isClosed) return this.messageCountLabel;
      return `${this.teamName} will keep you posted here as it moves along.`;
    },
    // Only the person who opened the ticket may rename it; the API holds to
    // the same.
    canEditSubject() {
      const accountId = this.$store.getters.account.data?.id;
      return Boolean(accountId) && accountId === (this.ticket.createdBy || this.ticket.user?.id);
    },
    canSaveSubject() {
      return !this.savingSubject && this.subjectDraft.trim().length > 0;
    },
  },
  methods: {
    timeAgo,
    closeModal() {
      this.$emit('closeViewTicketModal');
      this.$store.dispatch(CLEAR_TICKET_COMMENTS);
    },
    // What the conversation heard about the ticket goes into the list behind
    // the modal, so its stage, newest message and unread count stay right.
    onTicketUpdated(ticket) {
      this.$store.dispatch(UPSERT_TICKET, ticket);
    },
    onSent() {
      refreshTicketUnread();
    },
    onRead() {
      refreshTicketUnread();
    },
    // Assigned, or moved along: the conversation picks up the line the move
    // left, and the list and the badges follow.
    onTicketChanged() {
      this.$refs.conversation?.reload();
      this.$emit('refreshTickets');
      refreshTicketUnread();
    },
    // A move that needs no remark: staff starting on it, the organization
    // confirming a resolved ticket.
    async move(to, said) {
      if (this.moving) return;
      this.moving = true;
      let updated = null;
      try {
        updated = await this.$store.dispatch(SET_TICKET_STATUS, { id: this.ticket.id, to });
      } finally {
        this.moving = false;
      }
      if (!updated) return;
      this.$toast.success(said, { ...TOAST_GLOBAL_CONFIG, duration: 2500 });
      this.onTicketChanged();
    },
    startProgress() {
      return this.move(TICKET_MOVE_IN_PROGRESS, this.ticket.assignedTo?.id ? 'Marked in progress.' : 'Assigned to you and marked in progress.');
    },
    confirmResolved() {
      return this.move(TICKET_MOVE_CLOSED, 'Thank you — the ticket is closed.');
    },
    startSubjectEdit() {
      this.subjectDraft = this.ticket.subject || '';
      this.editingSubject = true;
      this.$nextTick(() => {
        this.$refs.subjectInput?.focus();
        this.$refs.subjectInput?.select();
      });
    },
    cancelSubjectEdit() {
      if (!this.savingSubject) this.stopSubjectEdit();
    },
    stopSubjectEdit() {
      this.editingSubject = false;
      this.$nextTick(() => this.$refs.editSubjectButton?.focus());
    },
    // The new title goes into the list and the open conversation as soon as
    // it is saved. If it could not be, the field stays open with it.
    async saveSubject() {
      if (!this.canSaveSubject) return;
      const subject = this.subjectDraft.trim();
      if (subject === this.ticket.subject) {
        this.stopSubjectEdit();
        return;
      }
      this.savingSubject = true;
      let ok = false;
      try {
        ok = await this.$store.dispatch(EDIT_TICKET, { id: this.ticket.id, subject });
      } finally {
        this.savingSubject = false;
      }
      if (ok) this.stopSubjectEdit();
      else this.$nextTick(() => this.$refs.subjectInput?.focus());
    },
    // A file let go of outside the conversation (the header, the footer, the
    // backdrop, a remark dialog) is refused, instead of the browser opening it
    // in place of the app. Over the conversation, the conversation has already
    // taken it; dragged text is left alone.
    onStrayDrag(event) {
      if (event.defaultPrevented || !isFileDrag(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'none';
    },
    onStrayDrop(event) {
      if (isFileDrag(event)) event.preventDefault();
    },
  },
  mounted() {
    this.$refs.dialog?.focus();
  },
};
</script>
<style scoped>
/* A conversation needs room: the dialog is as tall as the viewport allows,
   whatever the thread holds, so the composer is always at the same place. */
.modal-dialog.dn-modal-dialog.vt-dialog {
  max-width: min(880px, 94%);
  margin: 4vh auto;
}

.modal-content.dn-modal-content.vt-content {
  height: min(92vh, calc(100dvh - 32px));
  max-height: min(92vh, calc(100dvh - 32px));
}

.vt-header {
  padding-bottom: 14px;
  border-bottom: 1px solid var(--color-divider);
}

.dn-modal-header-actions {
  align-items: center;
  gap: 8px;
}

/* ------------------------------ the title -------------------------------- */
.vt-title-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
}

/* While it is renamed, the field takes the width the header has. */
.vt-header--editing .dn-modal-heading,
.vt-header--editing .dn-modal-heading-text {
  flex: 1 1 auto;
}

/* Pulled up a little, so opening the field hardly moves the header. */
.vt-title-form {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  margin-top: -3px;
}

/* Over the global input size (index.css, !important): the title's height,
   and 16px text, which iOS does not zoom into. */
.vt-title-input.form-control {
  flex: 1 1 auto;
  min-width: 0;
  height: 30px !important;
  padding: 0 8px;
  font-size: 1rem !important;
  font-weight: 600;
  line-height: 1.2;
  color: var(--color-text-primary);
}

.vt-title-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease;
}

.vt-title-btn:hover:not(:disabled) {
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
}

.vt-title-btn:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus);
}

.vt-title-btn:disabled {
  opacity: .55;
  cursor: default;
}

.vt-title-btn .pi {
  margin: 0;
  font-size: 0.85rem;
}

.vt-title-btn--save,
.vt-title-btn--save:hover:not(:disabled) {
  color: var(--color-primary);
}

/* The pencil sits on the title's first line. */
.vt-title-row .vt-title-btn {
  width: 26px;
  height: 26px;
  margin-top: -2px;
}

.vt-title-row .vt-title-btn .pi {
  font-size: 0.78rem;
}

/* ---------------------------- the lifecycle ------------------------------ */
.vt-lifecycle {
  flex: none;
  padding: 14px 24px 12px;
  border-bottom: 1px solid var(--color-divider);
  background: var(--color-bg-subtle, #FAFAFB);
}

/* The conversation lays itself out: the thread scrolls, the composer stays. */
.dn-modal-body.vt-body {
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}

/* ------------------------------ the footer ------------------------------- */
.vt-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 0;
}

.vt-footer__meta {
  flex: 1 1 auto;
  min-width: 0;
  color: var(--color-text-muted);
  font-size: 0.75rem;
}

.vt-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}

.vt-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
}

.vt-btn .pi {
  margin: 0;
  font-size: 0.75rem;
}

.dn-modal-footer .vt-btn--close {
  color: var(--color-danger) !important;
}

.dn-modal-footer .vt-btn--close:hover:not(:disabled) {
  background-color: #FEECEC !important;
  border-color: #F9C9C9 !important;
  color: var(--color-danger) !important;
}

.dn-modal-footer .btn.vt-btn--resolve,
.dn-modal-footer .btn.vt-btn--resolve:hover:not(:disabled) {
  border-color: #0E9F6E !important;
  background: #0E9F6E !important;
  color: #FFFFFF !important;
}

.dn-modal-footer .btn.vt-btn--resolve:hover:not(:disabled) {
  filter: brightness(0.94);
}

/* Resolved, on the organization's side: the question, in green. */
.vt-footer--confirm {
  background: #F2FBF7;
}

.vt-confirm {
  display: flex;
  flex: 1 1 260px;
  align-items: center;
  gap: 10px;
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 0.82rem;
  line-height: 1.4;
}

.vt-confirm .pi {
  flex: none;
  color: #0E9F6E;
  font-size: 1rem;
}

.vt-confirm strong {
  color: var(--color-text-primary);
  font-weight: 600;
}

@media (max-width: 991.98px) {
  .modal-dialog.dn-modal-dialog.vt-dialog {
    max-width: 96%;
    margin: 10px auto;
  }

  .modal-content.dn-modal-content.vt-content {
    height: calc(100dvh - 20px);
    max-height: calc(100dvh - 20px);
  }
}

@media (max-width: 575.98px) {
  .modal-dialog.dn-modal-dialog.vt-dialog {
    max-width: 100%;
    margin: 0;
  }

  .modal-content.dn-modal-content.vt-content {
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0 !important;
  }

  .vt-header {
    padding-bottom: 10px;
  }

  .vt-lifecycle {
    padding: 10px 16px;
  }

  /* A long subject takes two lines at most; the rest of the header stays
     one line, so the conversation keeps the room. */
  .vt-header .dn-modal-title {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 1rem;
  }

  .vt-header .dn-modal-subtitle {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* A phone's header is narrow: the status steps aside while the title is
     renamed, so the field has the room. */
  .vt-header--editing .status-pill,
  .vt-header--editing .dn-modal-header-actions > :not(.dn-modal-close) {
    display: none;
  }

  .vt-footer__meta {
    display: none;
  }

  .vt-actions {
    width: 100%;
  }

  .vt-actions .vt-btn {
    flex: 1 1 auto;
  }
}
</style>
